import React, { useMemo, useState } from "react";

// Simple moving average
function sma(values, period) {
  const out = Array(values.length).fill(null);
  for (let i = period - 1; i < values.length; i++) {
    let s = 0;
    for (let j = i - period + 1; j <= i; j++) s += values[j];
    out[i] = s / period;
  }
  return out;
}

function ema(values, period) {
  const out = Array(values.length).fill(null);
  const k = 2 / (period + 1);
  let prev;
  for (let i = 0; i < values.length; i++) {
    if (i === 0) { prev = values[0]; out[i] = prev; continue; }
    prev = values[i] * k + prev * (1 - k);
    out[i] = prev;
  }
  return out;
}

const BULL = "#10B981";
const BEAR = "#EF4444";

export function CandleChart({ candles, height = 420, overlays = {}, showVolume = true }) {
  const [hover, setHover] = useState(null);
  const width = 1000;
  const padL = 8, padR = 62, padT = 12;
  const volH = showVolume ? 70 : 0;
  const chartH = height - padT - volH - 24;

  const { min, max, closes, smaLine, emaLine, maxVol } = useMemo(() => {
    if (!candles || !candles.length) return { min: 0, max: 1, closes: [], maxVol: 1 };
    let mn = Infinity, mx = -Infinity, mv = 0;
    candles.forEach((c) => { mn = Math.min(mn, c.l); mx = Math.max(mx, c.h); mv = Math.max(mv, c.v || 0); });
    const pad = (mx - mn) * 0.08;
    const cl = candles.map((c) => c.c);
    return {
      min: mn - pad, max: mx + pad, closes: cl, maxVol: mv || 1,
      smaLine: overlays.sma ? sma(cl, overlays.sma) : null,
      emaLine: overlays.ema ? ema(cl, overlays.ema) : null,
    };
  }, [candles, overlays.sma, overlays.ema]);

  if (!candles || !candles.length) {
    return <div className="flex items-center justify-center text-slate-500" style={{ height }}>Loading chart…</div>;
  }

  const n = candles.length;
  const plotW = width - padL - padR;
  const step = plotW / n;
  const bw = Math.max(1.5, step * 0.62);
  const yOf = (p) => padT + (max - p) / (max - min) * chartH;
  const xOf = (i) => padL + i * step + step / 2;

  const priceToLine = (line) => line ? line.map((v, i) => (v == null ? null : `${xOf(i)},${yOf(v)}`)).filter(Boolean).join(" ") : "";

  const gridLines = 5;
  const decimals = max < 10 ? 4 : 2;

  return (
    <div className="relative w-full">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height: "auto" }}
        onMouseLeave={() => setHover(null)}>
        {/* grid */}
        {Array.from({ length: gridLines + 1 }).map((_, i) => {
          const y = padT + (chartH / gridLines) * i;
          const price = max - ((max - min) / gridLines) * i;
          return (
            <g key={i}>
              <line x1={padL} y1={y} x2={width - padR} y2={y} stroke="#1E293B" strokeWidth="1" />
              <text x={width - padR + 6} y={y + 4} fill="#64748B" fontSize="11" fontFamily="JetBrains Mono">{price.toFixed(decimals)}</text>
            </g>
          );
        })}

        {/* candles */}
        {candles.map((c, i) => {
          const up = c.c >= c.o;
          const color = up ? BULL : BEAR;
          const x = xOf(i);
          const yH = yOf(c.h), yL = yOf(c.l);
          const yO = yOf(c.o), yC = yOf(c.c);
          const top = Math.min(yO, yC);
          const bh = Math.max(1, Math.abs(yC - yO));
          return (
            <g key={i} onMouseEnter={() => setHover({ ...c, i, x })}>
              <line x1={x} y1={yH} x2={x} y2={yL} stroke={color} strokeWidth="1" />
              <rect x={x - bw / 2} y={top} width={bw} height={bh} fill={color} />
              <rect x={x - step / 2} y={padT} width={step} height={chartH} fill="transparent" />
            </g>
          );
        })}

        {/* overlays */}
        {smaLine && <polyline points={priceToLine(smaLine)} fill="none" stroke="#EAB308" strokeWidth="1.6" opacity="0.9" />}
        {emaLine && <polyline points={priceToLine(emaLine)} fill="none" stroke="#8B5CF6" strokeWidth="1.6" opacity="0.9" />}

        {/* volume */}
        {showVolume && candles.map((c, i) => {
          const up = c.c >= c.o;
          const vh = ((c.v || 0) / maxVol) * (volH - 8);
          const y = height - vh - 6;
          return <rect key={`v${i}`} x={xOf(i) - bw / 2} y={y} width={bw} height={vh} fill={up ? BULL : BEAR} opacity="0.35" />;
        })}

        {/* crosshair */}
        {hover && (
          <line x1={hover.x} y1={padT} x2={hover.x} y2={height - volH} stroke="#475569" strokeWidth="1" strokeDasharray="3 3" />
        )}
      </svg>

      {hover && (
        <div className="absolute top-2 left-2 bg-slate-900/90 backdrop-blur border border-slate-700 rounded-lg px-3 py-2 text-xs font-mono flex flex-wrap gap-x-4 gap-y-1 pointer-events-none">
          <span className="text-slate-400">O <span className="text-slate-200">{hover.o}</span></span>
          <span className="text-slate-400">H <span className="text-bull">{hover.h}</span></span>
          <span className="text-slate-400">L <span className="text-bear">{hover.l}</span></span>
          <span className="text-slate-400">C <span className="text-slate-200">{hover.c}</span></span>
          <span className="text-slate-400">V <span className="text-slate-300">{Math.round(hover.v)}</span></span>
        </div>
      )}
    </div>
  );
}

export default CandleChart;
